import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Box,Button,CircularProgress,Collapse,Dialog,DialogActions,DialogContent,DialogTitle,FormControl,FormControlLabel,IconButton,InputLabel,MenuItem,Select,Stack,Switch,TextField,Typography } from "@mui/material";
import { useEffect,useState } from "react";

const WEEKDAYS=[
  {value:1,label:"Lunes"},
  {value:2,label:"Martes"},
  {value:3,label:"Miércoles"},
  {value:4,label:"Jueves"},
  {value:5,label:"Viernes"},
  {value:6,label:"Sábado"},
  {value:7,label:"Domingo"}
];

const EMPTY_RANGE={weekday:1,entry_time:"08:00",exit_time:"17:00",lunch_enabled:false,lunch_start:"12:00",lunch_end:"13:00",break_minutes:0,crosses_midnight:false,is_active:true};
const EMPTY_SCHEDULE={id:null,name:"",is_active:true,ranges:[{...EMPTY_RANGE}]};
const EMPTY_ASSIGNMENT={id:null,person:"",schedule:"",temporary:false,valid_from:"",valid_until:"",is_active:true};

function getApiMessage(data,fallback){
  if(typeof data?.message==="string")return data.message;
  if(typeof data?.detail==="string")return data.detail;
  if(data?.errors&&typeof data.errors==="object")return Object.values(data.errors).flat().join(" ");
  return fallback;
}

function normalizeTime(value,fallback){
  if(!value)return fallback;
  return String(value).slice(0,5);
}

export default function AttendanceModals({fetchUser,schedules,people,scheduleModal,assignmentModal,onCloseSchedule,onCloseAssignment,onSaved,onError}){
  const [scheduleForm,setScheduleForm]=useState(EMPTY_SCHEDULE);
  const [assignmentForm,setAssignmentForm]=useState(EMPTY_ASSIGNMENT);
  const [savingSchedule,setSavingSchedule]=useState(false);
  const [savingAssignment,setSavingAssignment]=useState(false);

  useEffect(()=>{
    const schedule=scheduleModal.schedule;
    if(!scheduleModal.open){
      setScheduleForm(EMPTY_SCHEDULE);
      return;
    }
    if(!schedule){
      setScheduleForm({...EMPTY_SCHEDULE,ranges:[{...EMPTY_RANGE}]});
      return;
    }
    setScheduleForm({
      id:schedule.id,
      name:schedule.name||"",
      is_active:schedule.is_active===true,
      ranges:Array.isArray(schedule.ranges)&&schedule.ranges.length?schedule.ranges.map((range)=>({
        weekday:Number(range.weekday),
        entry_time:normalizeTime(range.entry_time,"08:00"),
        exit_time:normalizeTime(range.exit_time,"17:00"),
        lunch_enabled:Boolean(range.lunch_start&&range.lunch_end),
        lunch_start:normalizeTime(range.lunch_start,"12:00"),
        lunch_end:normalizeTime(range.lunch_end,"13:00"),
        break_minutes:Number(range.break_minutes||0),
        crosses_midnight:range.crosses_midnight===true,
        is_active:range.is_active!==false
      })):[{...EMPTY_RANGE}]
    });
  },[scheduleModal]);

  useEffect(()=>{
    const assignment=assignmentModal.assignment;
    if(!assignmentModal.open){
      setAssignmentForm(EMPTY_ASSIGNMENT);
      return;
    }
    if(!assignment){
      setAssignmentForm({...EMPTY_ASSIGNMENT});
      return;
    }
    setAssignmentForm({
      id:assignment.id,
      person:assignment.person?.id||"",
      schedule:assignment.schedule?.id||"",
      temporary:Boolean(assignment.valid_from||assignment.valid_until),
      valid_from:assignment.valid_from||"",
      valid_until:assignment.valid_until||"",
      is_active:assignment.is_active!==false
    });
  },[assignmentModal]);

  const readJson=async(response)=>{
    try{return await response.json();}catch{return {};}
  };

  const addRange=()=>{
    const usedDays=scheduleForm.ranges.map((range)=>Number(range.weekday));
    const available=WEEKDAYS.find((day)=>!usedDays.includes(day.value));
    if(!available){
      onError("Ya añadiste los siete días de la semana.");
      return;
    }
    setScheduleForm((previous)=>({...previous,ranges:[...previous.ranges,{...EMPTY_RANGE,weekday:available.value}]}));
  };

  const updateRange=(index,field,value)=>{
    setScheduleForm((previous)=>({...previous,ranges:previous.ranges.map((range,rangeIndex)=>rangeIndex===index?{...range,[field]:value}:range)}));
  };

  const removeRange=(index)=>{
    if(scheduleForm.ranges.length===1)return;
    setScheduleForm((previous)=>({...previous,ranges:previous.ranges.filter((_,rangeIndex)=>rangeIndex!==index)}));
  };

  const replicateSchedule=()=>{
    if(!scheduleForm.ranges.length){
      onError("Primero debes configurar al menos un día.");
      return;
    }
    const source=scheduleForm.ranges[0];
    setScheduleForm((previous)=>({
      ...previous,
      ranges:WEEKDAYS.map((day)=>({
        weekday:day.value,
        entry_time:source.entry_time,
        exit_time:source.exit_time,
        lunch_enabled:source.lunch_enabled,
        lunch_start:source.lunch_start,
        lunch_end:source.lunch_end,
        break_minutes:source.break_minutes,
        crosses_midnight:source.crosses_midnight,
        is_active:source.is_active
      }))
    }));
  };

  const saveSchedule=async()=>{
    if(!scheduleForm.name.trim()){
      onError("El nombre del horario es obligatorio.");
      return;
    }
    if(!scheduleForm.ranges.length){
      onError("Debes añadir al menos un día.");
      return;
    }
    const weekdays=scheduleForm.ranges.map((range)=>Number(range.weekday));
    if(new Set(weekdays).size!==weekdays.length){
      onError("No puedes repetir el mismo día.");
      return;
    }
    for(const range of scheduleForm.ranges){
      if(!range.entry_time||!range.exit_time){
        onError("Todos los días deben tener hora de entrada y salida.");
        return;
      }
      if(!range.crosses_midnight&&range.entry_time>=range.exit_time){
        onError("La hora de salida debe ser posterior a la hora de entrada.");
        return;
      }
      if(range.lunch_enabled&&(!range.lunch_start||!range.lunch_end)){
        onError("Debes ingresar el inicio y el fin del lunch.");
        return;
      }
      if(range.lunch_enabled&&range.lunch_start>=range.lunch_end){
        onError("La hora final del lunch debe ser posterior a la hora inicial.");
        return;
      }
      if(range.lunch_enabled&&!range.crosses_midnight&&(range.lunch_start<=range.entry_time||range.lunch_end>=range.exit_time)){
        onError("El lunch debe estar dentro del horario de trabajo.");
        return;
      }
    }

    setSavingSchedule(true);
    try{
      const editing=Boolean(scheduleForm.id);
      const response=await fetchUser(editing?`/attendance/api/schedules/${scheduleForm.id}/`:"/attendance/api/schedules/",{
        method:editing?"PUT":"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          name:scheduleForm.name.trim(),
          is_active:scheduleForm.is_active,
          ranges:scheduleForm.ranges.map((range)=>({
            weekday:Number(range.weekday),
            entry_time:range.entry_time,
            exit_time:range.exit_time,
            lunch_start:range.lunch_enabled?range.lunch_start:null,
            lunch_end:range.lunch_enabled?range.lunch_end:null,
            break_minutes:range.lunch_enabled?0:Number(range.break_minutes||0),
            crosses_midnight:range.crosses_midnight,
            is_active:range.is_active
          }))
        })
      });
      const data=await readJson(response);
      if(!response.ok||data.ok!==true)throw new Error(getApiMessage(data,editing?"No se pudo actualizar el horario.":"No se pudo crear el horario."));
      onCloseSchedule();
      await onSaved(data.message||(editing?"Horario actualizado correctamente.":"Horario creado correctamente."));
    }catch(error){
      onError(error instanceof Error?error.message:"No se pudo guardar el horario.");
    }finally{
      setSavingSchedule(false);
    }
  };

  const saveAssignment=async()=>{
    if(!assignmentForm.person){
      onError("Debes seleccionar un usuario.");
      return;
    }
    if(!assignmentForm.schedule){
      onError("Debes seleccionar un horario.");
      return;
    }
    if(assignmentForm.temporary&&!assignmentForm.valid_from&&!assignmentForm.valid_until){
      onError("Ingresa al menos una fecha para la asignación temporal.");
      return;
    }
    if(assignmentForm.temporary&&assignmentForm.valid_from&&assignmentForm.valid_until&&assignmentForm.valid_from>assignmentForm.valid_until){
      onError("La fecha final no puede ser anterior a la fecha inicial.");
      return;
    }

    setSavingAssignment(true);
    try{
      const editing=Boolean(assignmentForm.id);
      const response=await fetchUser(editing?`/attendance/api/assignments/${assignmentForm.id}/`:"/attendance/api/assignments/",{
        method:editing?"PUT":"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          person:Number(assignmentForm.person),
          schedule:Number(assignmentForm.schedule),
          valid_from:assignmentForm.temporary&&assignmentForm.valid_from?assignmentForm.valid_from:null,
          valid_until:assignmentForm.temporary&&assignmentForm.valid_until?assignmentForm.valid_until:null,
          is_active:assignmentForm.is_active
        })
      });
      const data=await readJson(response);
      if(!response.ok||data.ok!==true)throw new Error(getApiMessage(data,editing?"No se pudo actualizar la asignación.":"No se pudo asignar el horario."));
      onCloseAssignment();
      await onSaved(data.message||(editing?"Asignación actualizada correctamente.":"Horario asignado correctamente."));
    }catch(error){
      onError(error instanceof Error?error.message:"No se pudo guardar la asignación.");
    }finally{
      setSavingAssignment(false);
    }
  };

  return (
    <>
      <Dialog open={scheduleModal.open} onClose={savingSchedule?undefined:onCloseSchedule} fullWidth maxWidth="md">
        <DialogTitle sx={{fontWeight:800}}>{scheduleForm.id?"Editar horario de asistencia":"Nuevo horario de asistencia"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{paddingTop:1}}>
            <TextField fullWidth required label="Nombre del horario" placeholder="Ejemplo: Administrativo" value={scheduleForm.name} onChange={(event)=>setScheduleForm((previous)=>({...previous,name:event.target.value}))} />
            <Box sx={{minHeight:52,display:"flex",alignItems:"center",justifyContent:"space-between",paddingX:1.5,border:"1px solid #e5e7eb",borderRadius:2}}>
              <Box>
                <Typography sx={{fontSize:14,fontWeight:700}}>Horario activo</Typography>
                <Typography sx={{fontSize:12,color:"#6b7280"}}>Los usuarios asignados utilizarán este horario.</Typography>
              </Box>
              <Switch checked={scheduleForm.is_active} onChange={(event)=>setScheduleForm((previous)=>({...previous,is_active:event.target.checked}))} />
            </Box>
            <Box>
              <Box sx={{display:"flex",alignItems:{xs:"stretch",sm:"center"},justifyContent:"space-between",flexDirection:{xs:"column",sm:"row"},gap:1.5,marginBottom:1.5}}>
                <Box>
                  <Typography sx={{fontSize:18,fontWeight:800}}>Días y horas</Typography>
                  <Typography sx={{fontSize:13,color:"#6b7280",marginTop:0.3}}>Configura el primer día y replica ese horario si deseas usarlo toda la semana.</Typography>
                </Box>
                <Stack direction={{xs:"column",sm:"row"}} spacing={1}>
                  <Button variant="outlined" startIcon={<ContentCopyRoundedIcon />} onClick={replicateSchedule}>Replicar horario</Button>
                  <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={addRange} disabled={scheduleForm.ranges.length>=7}>Añadir día</Button>
                </Stack>
              </Box>
              <Stack spacing={1.5}>
                {scheduleForm.ranges.map((range,index)=>(
                  <Box key={`${range.weekday}-${index}`} sx={{padding:1.5,border:"1px solid #e5e7eb",borderRadius:2,backgroundColor:"#f9fafb"}}>
                    <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"1.3fr repeat(2,minmax(0,1fr)) auto"},alignItems:"center",gap:1.2}}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Día</InputLabel>
                        <Select value={range.weekday} label="Día" onChange={(event)=>updateRange(index,"weekday",Number(event.target.value))}>
                          {WEEKDAYS.map((weekday)=><MenuItem key={weekday.value} value={weekday.value}>{weekday.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <TextField fullWidth size="small" type="time" label="Entrada" value={range.entry_time} onChange={(event)=>updateRange(index,"entry_time",event.target.value)} InputLabelProps={{shrink:true}} />
                      <TextField fullWidth size="small" type="time" label="Salida" value={range.exit_time} onChange={(event)=>updateRange(index,"exit_time",event.target.value)} InputLabelProps={{shrink:true}} />
                      <IconButton color="error" disabled={scheduleForm.ranges.length===1} onClick={()=>removeRange(index)}><DeleteOutlineRoundedIcon /></IconButton>
                    </Box>
                    <Box sx={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:2,marginTop:1.5}}>
                      <Typography sx={{fontSize:14,fontWeight:700}}>Incluir lunch</Typography>
                      <Switch checked={range.lunch_enabled} onChange={(event)=>updateRange(index,"lunch_enabled",event.target.checked)} />
                    </Box>
                    {range.lunch_enabled?(
                      <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(2,minmax(0,1fr))"},gap:1.2,marginTop:1}}>
                        <TextField fullWidth size="small" type="time" label="Inicio del lunch" value={range.lunch_start} onChange={(event)=>updateRange(index,"lunch_start",event.target.value)} InputLabelProps={{shrink:true}} />
                        <TextField fullWidth size="small" type="time" label="Fin del lunch" value={range.lunch_end} onChange={(event)=>updateRange(index,"lunch_end",event.target.value)} InputLabelProps={{shrink:true}} />
                      </Box>
                    ):(
                      <TextField fullWidth size="small" type="number" label="Minutos de descanso opcional" value={range.break_minutes} onChange={(event)=>updateRange(index,"break_minutes",Math.max(0,Number(event.target.value||0)))} inputProps={{min:0}} sx={{marginTop:1}} />
                    )}
                    <Box sx={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:2,marginTop:1.5}}>
                      <Typography sx={{fontSize:14,fontWeight:700}}>Turno cruza medianoche</Typography>
                      <Switch checked={range.crosses_midnight} onChange={(event)=>updateRange(index,"crosses_midnight",event.target.checked)} />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{padding:2}}>
          <Button onClick={onCloseSchedule} disabled={savingSchedule}>Cancelar</Button>
          <Button variant="contained" onClick={saveSchedule} disabled={savingSchedule}>{savingSchedule?<CircularProgress size={20} sx={{color:"#ffffff"}} />:scheduleForm.id?"Guardar cambios":"Crear horario"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignmentModal.open} onClose={savingAssignment?undefined:onCloseAssignment} fullWidth maxWidth="sm">
        <DialogTitle sx={{fontWeight:800}}>{assignmentForm.id?"Editar asignación":"Asignar horario de asistencia"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{paddingTop:1}}>
            <FormControl fullWidth>
              <InputLabel>Usuario</InputLabel>
              <Select value={assignmentForm.person} label="Usuario" disabled={Boolean(assignmentForm.id)} onChange={(event)=>setAssignmentForm((previous)=>({...previous,person:event.target.value}))}>
                {people.map((person)=><MenuItem key={person.id} value={person.id}>{person.name} - {person.identification}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Horario</InputLabel>
              <Select value={assignmentForm.schedule} label="Horario" onChange={(event)=>setAssignmentForm((previous)=>({...previous,schedule:event.target.value}))}>
                {schedules.filter((schedule)=>schedule.is_active||schedule.id===Number(assignmentForm.schedule)).map((schedule)=><MenuItem key={schedule.id} value={schedule.id}>{schedule.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={{padding:1.5,border:"1px solid #e5e7eb",borderRadius:2}}>
              <FormControlLabel control={<Switch checked={assignmentForm.temporary} onChange={(event)=>setAssignmentForm((previous)=>({...previous,temporary:event.target.checked,valid_from:event.target.checked?previous.valid_from:"",valid_until:event.target.checked?previous.valid_until:""}))} />} label={<Box><Typography sx={{fontSize:14,fontWeight:700}}>Asignación temporal</Typography><Typography sx={{fontSize:12,color:"#6b7280"}}>Actívalo solamente si este horario tendrá una vigencia limitada.</Typography></Box>} />
              <Collapse in={assignmentForm.temporary}>
                <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(2,minmax(0,1fr))"},gap:2,marginTop:1.5}}>
                  <TextField fullWidth type="date" label="Válido desde" value={assignmentForm.valid_from} onChange={(event)=>setAssignmentForm((previous)=>({...previous,valid_from:event.target.value}))} InputLabelProps={{shrink:true}} />
                  <TextField fullWidth type="date" label="Válido hasta" value={assignmentForm.valid_until} onChange={(event)=>setAssignmentForm((previous)=>({...previous,valid_until:event.target.value}))} InputLabelProps={{shrink:true}} />
                </Box>
              </Collapse>
              {!assignmentForm.temporary&&<Box sx={{display:"inline-flex",alignItems:"center",gap:0.7,marginTop:1,paddingX:1.1,paddingY:0.55,borderRadius:2,backgroundColor:"#f0fdf4",border:"1px solid #bbf7d0"}}><Box sx={{width:7,height:7,borderRadius:"50%",backgroundColor:"#16a34a"}} /><Typography sx={{color:"#166534",fontSize:11,fontWeight:800,lineHeight:1.2}}>Asignación indefinida</Typography></Box>}
            </Box>
            {assignmentForm.id&&(
              <Box sx={{minHeight:52,display:"flex",alignItems:"center",justifyContent:"space-between",paddingX:1.5,border:"1px solid #e5e7eb",borderRadius:2}}>
                <Typography sx={{fontSize:14,fontWeight:700}}>Asignación activa</Typography>
                <Switch checked={assignmentForm.is_active} onChange={(event)=>setAssignmentForm((previous)=>({...previous,is_active:event.target.checked}))} />
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{padding:2}}>
          <Button onClick={onCloseAssignment} disabled={savingAssignment}>Cancelar</Button>
          <Button variant="contained" onClick={saveAssignment} disabled={savingAssignment}>{savingAssignment?<CircularProgress size={20} sx={{color:"#ffffff"}} />:assignmentForm.id?"Guardar cambios":"Asignar horario"}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}